import { useRef, useState, type ReactElement, type ReactNode } from 'react'

import { ExpandMore as ExpandMoreIcon } from '@mui/icons-material'
import { Accordion, AccordionDetails, AccordionSummary, Alert, Stack, Typography, alpha, styled } from '@mui/material'
import type ViewProductStore from '../../../AppData/_stores/ViewProductStore'

const MOBILE_BREAKPOINT = 760

export const InfoDialogContent = styled('div')({
  padding: '25px',
  boxSizing: 'border-box',
  width: '100%',
  maxHeight: 'calc(100vh - 150px)',
  overflow: 'auto',

  ['@media (max-width: ' + MOBILE_BREAKPOINT + 'px)']: {
    padding: '20px',
    maxHeight: 'calc(100vh - 128px)'
  }
})

export const InfoGroup = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: 18
})

const InfoAccordion = styled(Accordion)(({ theme }) => ({
  border: '1px solid rgba(0, 0, 0, 0.12)',
  boxShadow: 'none',

  '&:before': {
    display: 'none'
  },

  '&.Mui-expanded': {
    borderColor: alpha(theme.palette.primary.main, 0.45),
    margin: 0
  },

  '& + &': {
    marginTop: 8
  }
}))

const InfoAccordionSummary = styled(AccordionSummary)({
  minHeight: 44,

  '&.Mui-expanded': {
    minHeight: 44
  },

  '& .MuiAccordionSummary-content': {
    margin: '10px 0',

    '&.Mui-expanded': {
      margin: '10px 0'
    }
  }
})

const InfoAccordionDetails = styled(AccordionDetails)({
  padding: '0 16px 18px'
})

export const InfoSection = styled('section')({
  display: 'flex',
  flexDirection: 'column',
  gap: 10,
  paddingTop: 16,
  borderTop: '1px solid rgba(0, 0, 0, 0.12)',

  '&:first-of-type': {
    paddingTop: 0,
    borderTop: 0
  }
})

export const InfoSectionTitle = styled('div')({
  fontSize: 14,
  fontWeight: 600,
  color: '#222'
})

export const InfoSectionFields = styled('div')({
  display: 'grid',
  gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
  gap: '12px 24px',

  ['@media (max-width: ' + MOBILE_BREAKPOINT + 'px)']: {
    gridTemplateColumns: '1fr'
  }
})

export const InfoField = styled('div')({
  display: 'flex',
  flexDirection: 'column',
  gap: 2,

  '&.wide': {
    gridColumn: '1 / -1'
  }
})

export const InfoLabel = styled('div')({
  fontSize: 12,
  fontWeight: 600,
  color: '#555'
})

export const InfoValue = styled('div')({
  fontSize: 13,
  lineHeight: 1.4,
  overflowWrap: 'anywhere',
  whiteSpace: 'pre-wrap'
})

export interface RenderedProductInfoViewProduct {
  renderedContent: ReactElement
  viewProduct: ViewProductStore
}

interface ProductInfoViewProductListProps {
  emptyMessage: ReactNode
  renderedViewProducts: RenderedProductInfoViewProduct[]
}

export function ProductInfoViewProductList(props: ProductInfoViewProductListProps) {
  const [expandedViewProductId, setExpandedViewProductId] = useState<string | null | undefined>(undefined)
  const contentRef = useRef<HTMLDivElement>(null)
  const pendingScrollId = useRef<string | null>(null)

  if (props.renderedViewProducts.length === 0) {
    return <InfoDialogContent>
      <Alert severity="info">
        {props.emptyMessage}
      </Alert>
    </InfoDialogContent>
  }

  if (props.renderedViewProducts.length === 1) {
    return <InfoDialogContent>
      <InfoGroup>
        {props.renderedViewProducts[0].renderedContent}
      </InfoGroup>
    </InfoDialogContent>
  }

  const renderedViewProductIds = props.renderedViewProducts.map(item => String(item.viewProduct.get('id')))
  const activeExpandedViewProductId = expandedViewProductId === null
    ? null
    : expandedViewProductId !== undefined && renderedViewProductIds.includes(expandedViewProductId)
      ? expandedViewProductId
      : renderedViewProductIds[0]

  return <InfoDialogContent ref={contentRef}>
    <Stack spacing={1}>
      {props.renderedViewProducts.map(item => {
        const viewProductId = String(item.viewProduct.get('id'))

        return <InfoAccordion
          key={viewProductId}
          disableGutters
          expanded={activeExpandedViewProductId === viewProductId}
          slotProps={{
            transition: {
              // Match opening/closing durations so the previous section has collapsed
              // before measuring the newly opened header.
              timeout: 200,
              onEntered: element => {
                requestAnimationFrame(() => {
                  if (pendingScrollId.current !== viewProductId) return

                  const container = contentRef.current
                  const accordion = element.parentElement
                  if (!container || !accordion) return

                  pendingScrollId.current = null
                  container.scrollTo({
                    top: container.scrollTop + accordion.getBoundingClientRect().top
                      - container.getBoundingClientRect().top
                      - parseFloat(getComputedStyle(container).paddingTop),
                    behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth'
                  })
                })
              }
            }
          }}
          onChange={(_event, isExpanded) => {
            pendingScrollId.current = isExpanded ? viewProductId : null
            setExpandedViewProductId(isExpanded ? viewProductId : null)
          }}
        >
          <InfoAccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle2">{item.viewProduct.get('name')}</Typography>
          </InfoAccordionSummary>
          <InfoAccordionDetails>
            <InfoGroup>
              {item.renderedContent}
            </InfoGroup>
          </InfoAccordionDetails>
        </InfoAccordion>
      })}
    </Stack>
  </InfoDialogContent>
}
